import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { locationSettingsKeys } from '~/domain/LocationSettings';
import { useAppSelector } from '~/hooks/useAppSelector';
import type { IUser } from '~/models/User';
import { hasChildOffices } from '~/utils/OfficeUtils';

/**
 * The searchable locations list of a multi-location account. A row edited
 * from its page reads from that page's cache, so going back to the list shows
 * the saved name and address without a refetch; redux only refreshes the
 * selected office.
 */
export const useLocationList = () => {
  const queryClient = useQueryClient();
  const childOffices = useAppSelector((state) => state.user.childOffices);
  const masterOfficeId = useAppSelector((state) => state.user.user?.id);
  const [query, setQuery] = useState('');

  const locations = useMemo(
    () =>
      (childOffices ?? []).map(
        (office: IUser) =>
          queryClient.getQueryData<IUser>(locationSettingsKeys.office(office.id)) ?? office,
      ),
    [childOffices, queryClient],
  );

  const visibleLocations = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return locations;
    return locations.filter((office) => office.office_name?.toLowerCase().includes(term));
  }, [locations, query]);

  return {
    isMultiLocation: hasChildOffices(childOffices),
    locations: visibleLocations,
    masterOfficeId,
    query,
    setQuery,
  };
};
